import React, { memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
    faCube,
} from "@fortawesome/free-solid-svg-icons";
import { packagesApi, partitionPalettePackageGroups } from "../../../../services/packages";
import { PaletteDragHint } from "../PaletteDragHint";
import type { PackagePayload } from "../../../../services/packages";
import { subscribeToRegistry } from "../../../../registry";
import { usePackagePalette } from "../../../../providers/packages/PackagePaletteContext";
import { useNodeCatalogDrawer } from "../../../../providers/packages/NodeCatalogDrawerProvider";
import { useToastContext } from "../../../../providers/ToastProvider";
import { InstalledPackageAccordion } from "./InstalledPackageAccordion";
import { PaletteForkFamily } from "./PaletteForkFamily";
import { visiblePaletteTriggerPackagesCount, type PackagePaletteGroup } from "./model";
import { paletteDescriptorBootstrapKey } from "./registryBootstrap";
import { TOOLS_PALETTE_DROPDOWN_ATTR, TOOLS_PALETTE_PANEL_ATTR } from "../toolsPaletteDismiss";
import packageStyles from "./ToolsMenuPackagePalette.module.css";

function escapeCssAttrToken(coord: string): string {
    return typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(coord) : coord;
}

export const PackagesPaletteDropdown = memo(function PackagesPaletteDropdown({
    groups,
    open,
    setOpen,
}: {
    groups: PackagePaletteGroup[];
    open: boolean;
    setOpen: (open: boolean) => void;
}) {
    const packagePaletteScrollRef = useRef<HTMLDivElement>(null);
    const { openNodeCatalogDrawer } = useNodeCatalogDrawer();
    const { showToast } = useToastContext();
    const {
        activePackageKey,
        setActivePackageKey,
        paletteDockRevealCoord,
        setPaletteDockRevealCoord,
    } = usePackagePalette();

    const [forkManualPickByRoot, setForkManualPickByRoot] = useState<Record<string, string>>({});
    const [paletteCatalogSnapshot, setPaletteCatalogSnapshot] = useState<{
        publishedDirNames: Set<string>;
        installedByDir: Map<string, PackagePayload>;
        publishAllowed: boolean;
    } | null>(null);
    const [publishingPackageKey, setPublishingPackageKey] = useState<string | null>(null);
    const paletteCatalogRef = useRef(paletteCatalogSnapshot);
    paletteCatalogRef.current = paletteCatalogSnapshot;
    const paletteRows = useMemo(
        () => partitionPalettePackageGroups(groups),
        [groups],
    );

    const prevPaletteOpenRef = useRef(false);
    useEffect(() => {
        if (prevPaletteOpenRef.current && !open) {
            setPaletteDockRevealCoord(null);
        }
        prevPaletteOpenRef.current = open;
    }, [open, setPaletteDockRevealCoord]);
    const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

    const packageRegistryBootstrapKey = useSyncExternalStore(
        subscribeToRegistry,
        paletteDescriptorBootstrapKey,
        () => "ssr",
    );

    // No Escape / outside-click dismissal on purpose: the palette stays open
    // until its own trigger is clicked again (or the Data Catalog claims the
    // strip beside the rail).

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        void (async () => {
            try {
                const [cat, cap, installed] = await Promise.all([
                    packagesApi.catalog(),
                    packagesApi.factoryCapabilities(),
                    packagesApi.listInstalled(),
                ]);
                if (cancelled) return;
                setPaletteCatalogSnapshot({
                    publishedDirNames: new Set(cat.packages.map((p) => p.dirName)),
                    installedByDir: new Map(installed.packages.map((p) => [p.dirName, p])),
                    publishAllowed: cap.catalogPublish,
                });
            } catch (err) {
                if (cancelled) return;
                setPaletteCatalogSnapshot({
                    publishedDirNames: new Set(),
                    installedByDir: new Map(),
                    publishAllowed: true,
                });
                showToast((err as Error)?.message ?? "Could not load catalog metadata.", "warning");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, showToast, packageRegistryBootstrapKey]);

    useEffect(() => {
        if (paletteDockRevealCoord?.trim()) setOpen(true);
    }, [paletteDockRevealCoord, setOpen]);

    useEffect(() => {
        const coord = paletteDockRevealCoord?.trim();
        if (!open || !coord) return undefined;

        const scrollEl = packagePaletteScrollRef.current;
        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 48;

        const tryReveal = (): void => {
            if (cancelled || !scrollEl) return;
            const sel = `:scope > [data-package-palette-coords~="${escapeCssAttrToken(coord)}"]`;
            const anchor = scrollEl.querySelector<HTMLElement>(sel);
            attempts++;
            if (!anchor?.isConnected || !scrollEl.contains(anchor)) {
                if (attempts < maxAttempts) requestAnimationFrame(tryReveal);
                else setPaletteDockRevealCoord(null);
                return;
            }
            anchor.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
            const details = anchor.querySelector("details");
            if (details) details.open = true;
            setPaletteDockRevealCoord(null);
        };

        const id = window.requestAnimationFrame(tryReveal);
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(id);
        };
    }, [
        open,
        packageRegistryBootstrapKey,
        paletteDockRevealCoord,
        paletteRows,
        setPaletteDockRevealCoord,
    ]);

    const onPublishToCatalog = useCallback(
        async (dirName: string) => {
            const row = paletteCatalogRef.current?.installedByDir.get(dirName);
            if (!row) {
                showToast("Package metadata not loaded yet.", "warning");
                return;
            }
            setPublishingPackageKey(dirName);
            try {
                await packagesApi.publishToCatalog(dirName, { replace: true });
                setPaletteCatalogSnapshot((prev) => {
                    if (!prev) return prev;
                    const nextPublished = new Set(prev.publishedDirNames);
                    nextPublished.add(dirName);
                    return { ...prev, publishedDirNames: nextPublished };
                });
                showToast(`Published ${dirName} to the shared catalog.`, "success");
            } catch (e) {
                showToast((e as Error)?.message ?? "Publish failed.", "error");
            } finally {
                setPublishingPackageKey(null);
            }
        },
        [showToast],
    );

    const totalPackagesDisplayed = useMemo(
        () => visiblePaletteTriggerPackagesCount({ paletteRows }),
        [paletteRows],
    );

    // The rows render from the installed-package registry, which is ready before
    // the catalog snapshot is: until the snapshot lands, every row's summary is
    // still missing its CatalogPublishPill, and mounting that pill shifts the
    // Export/Edit buttons ~65px left - the actions share a flex cluster with the
    // row title, and the title is `flex: 1`, so it absorbs the slack. Anything
    // driving those buttons has to know the row has stopped growing first, so
    // say so rather than let it guess. See #334.
    const catalogSnapshotState = paletteCatalogSnapshot !== null ? "loaded" : "loading";

    return (
        <div
            id="packages-palette"
            className={packageStyles.packagePaletteRoot}
            {...{ [TOOLS_PALETTE_DROPDOWN_ATTR]: "true" }}
        >
            <div className={packageStyles.packagePaletteColumn}>
                <button
                    type="button"
                    className={`${packageStyles.packagePaletteTrigger} ${open ? packageStyles.packagePaletteTriggerOpen : ""}`}
                    onClick={toggle}
                    aria-expanded={open}
                    aria-haspopup="true"
                    data-tooltip={open ? "Close node package palette" : "Open node package palette"}
                >
                    <span className={packageStyles.packagePaletteTriggerTop}>
                        <FontAwesomeIcon icon={faCube} className={packageStyles.packagePaletteTriggerIcon} />
                        <span className={packageStyles.packagePaletteTriggerCount}>{totalPackagesDisplayed}</span>
                        <FontAwesomeIcon
                            icon={open ? faChevronLeft : faChevronRight}
                            className={packageStyles.packagePaletteTriggerChevron}
                        />
                    </span>
                    <span className={packageStyles.packagePaletteTriggerLabel}>Node Catalog</span>
                </button>
            </div>
            {open && (
                <div
                    className={packageStyles.packagePalettePanel}
                    role="region"
                    aria-label="Package templates"
                    data-curio-palette-catalog={catalogSnapshotState}
                    {...{ [TOOLS_PALETTE_PANEL_ATTR]: "true" }}
                >
                    <div className={packageStyles.packagePaletteToolbar}>
                        <div className={packageStyles.packagePalettePanelTitle}>NODE PACKAGES</div>
                    </div>
                    <div ref={packagePaletteScrollRef} className={packageStyles.packagePaletteScroll}>
                        {paletteRows.map((row) =>
                            row.kind === "singleton" ? (
                                <div
                                    key={row.group.key}
                                    className={packageStyles.packagePaletteRowAnchor}
                                    data-pkg-palette-coords={row.group.key}
                                >
                                    <InstalledPackageAccordion
                                        group={row.group}
                                        activePackageKey={activePackageKey}
                                        setActivePackageKey={setActivePackageKey}
                                        catalogMetadataLoaded={paletteCatalogSnapshot !== null}
                                        catalogPublishAllowed={paletteCatalogSnapshot?.publishAllowed ?? true}
                                        isCatalogPublished={
                                            paletteCatalogSnapshot?.publishedDirNames.has(row.group.key) ?? false
                                        }
                                        publishingPackageKey={publishingPackageKey}
                                        onPublishToCatalog={onPublishToCatalog}
                                    />
                                </div>
                            ) : (
                                <div
                                    key={`fork-family:${row.rootKey}`}
                                    className={packageStyles.packagePaletteRowAnchor}
                                    data-pkg-palette-coords={row.members.map((m) => m.key).join(" ")}
                                >
                                    <PaletteForkFamily
                                        rootKey={row.rootKey}
                                        members={row.members}
                                        activePackageKey={activePackageKey}
                                        setActivePackageKey={setActivePackageKey}
                                        forkManualPickByRoot={forkManualPickByRoot}
                                        setForkManualPickByRoot={setForkManualPickByRoot}
                                        catalogMetadataLoaded={paletteCatalogSnapshot !== null}
                                        catalogPublishAllowed={paletteCatalogSnapshot?.publishAllowed ?? true}
                                        publishingPackageKey={publishingPackageKey}
                                        catalogPublishedDirNames={paletteCatalogSnapshot?.publishedDirNames ?? null}
                                        onPublishToCatalog={onPublishToCatalog}
                                    />
                                </div>
                            ),
                        )}
                    </div>
                    <div className={packageStyles.packagePaletteFooter}>
                        <PaletteDragHint item="node" />
                        <button
                            type="button"
                            className={packageStyles.packageGetPackagesBtn}
                            data-tooltip="Browse and install node packages"
                            aria-label="Browse Node Catalog: open the Node Catalog drawer"
                            onClick={() => {
                                openNodeCatalogDrawer();
                            }}
                        >
                            Browse Node Catalog +
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});
